import { motion, useReducedMotion } from "framer-motion";
import { Package, Tag, Percent, TrendingUp, ShoppingCart, Truck } from "lucide-react";

/**
 * Landing editorial problem section ("Why Carriapp") — deepens the problem
 * Carriapp solves by laying out the six real-world frictions that make
 * "find the cheapest product" NOT the same as "minimize your basket total":
 *
 *   1. Format/size mismatch (price-per-unit isn't on the shelf)
 *   2. Private-label alternatives
 *   3. Promotions (2x1, second unit, card discounts, daily deals)
 *   4. Prices that move weekly
 *   5. Per-product cheap ≠ whole-basket cheap
 *   6. Delivery / membership fees also count
 *
 * The section sits between the Separator and HowToStart at the SAME
 * composition position formerly occupied by SaveOfferSection. Copy is
 * locked by the co-located test suite (see why-carriapp.test.tsx).
 *
 * Visual treatment is deliberately light: no Card wrappers around the
 * problem blocks, no heavy borders/backgrounds — just spacing and a
 * small semantically relevant lucide icon per block. Problem 1 carries
 * a small monospaced example (900 ml / 1 L / 1,5 L) so the format
 * comparison friction is concrete instead of abstract.
 */
interface Problem {
    title: string;
    description: string;
    example?: string[];
}

const PROBLEMS: Problem[] = [
    {
        title: "¿Realmente es más barato?",
        description:
            "El precio más bajo no siempre es el más conveniente por litro o kilo.",
        example: ["900 ml — $2.490", "1 L — $2.690", "1,5 L — $3.790"],
    },
    {
        title: "Las marcas propias cambian la ecuación",
        description:
            "Una alternativa equivalente puede costar bastante menos que la marca de siempre.",
    },
    {
        title: "Las promociones cambian todo",
        description:
            "2x1, segunda unidad, descuentos con tarjeta o promociones por día pueden cambiar cuál opción conviene.",
    },
    {
        title: "Los precios no se quedan quietos",
        description:
            "El supermercado más conveniente la semana pasada puede no serlo hoy.",
    },
    {
        title: "Un producto barato no hace una compra barata",
        description:
            "Comprar cada producto en la tienda más barata no significa armar la compra más barata.",
    },
    {
        title: "Despacho y beneficios también cuentan",
        description:
            "Dividir la compra entre tiendas puede salir más caro si sumas despachos y membresías perdidas.",
    },
];

const ICONS = [Package, Tag, Percent, TrendingUp, ShoppingCart, Truck];

export const WhyCarriapp = () => {
    const reduceMotion = useReducedMotion();

    return (
        <section className="relative isolate flex flex-col justify-center mt-6 lg:mt-12 mb-8 lg:mb-16">
            <div
                aria-hidden="true"
                className="absolute inset-y-0 left-1/2 w-screen -translate-x-1/2 z-[-1] bg-[#FEEDE1]"
            />
            <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                className="text-center pt-32 lg:pt-24"
            >
                <span className="text-primary text-sm font-semibold uppercase tracking-wider">
                    ¿POR QUÉ CARRIAPP?
                </span>
                <h2 className="text-2xl md:text-3xl xl:text-4xl font-bold tracking-tight mt-3 max-w-3xl mx-auto">
                    Ahorrar en el súper no es tan simple como comparar precios
                </h2>
                <p className="text-base xl:text-lg text-muted-foreground mt-4 max-w-2xl mx-auto leading-relaxed">
                    Formatos distintos, marcas propias, promociones que cambian y precios que se mueven todo el tiempo: saber dónde conviene comprar es más complejo que mirar un precio.
                </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12 mt-12 lg:mt-16">
                {PROBLEMS.map((p, i) => {
                    const Icon = ICONS[i];
                    return (
                        <motion.div
                            key={p.title}
                            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, amount: 0.3 }}
                            transition={{ duration: 0.5, delay: 0.05 * i }}
                            className="flex flex-col"
                        >
                            <Icon
                                className="w-7 h-7 text-primary mb-3"
                                aria-hidden="true"
                            />
                            <h3 className="text-lg font-bold mb-2">{p.title}</h3>
                            <p className="text-sm text-muted-foreground leading-relaxed">
                                {p.description}
                            </p>
                            {p.example && (
                                <div className="mt-3 flex flex-col gap-0.5 text-xs text-muted-foreground/80 font-mono">
                                    {p.example.map((line) => (
                                        <span key={line}>{line}</span>
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    );
                })}
            </div>

            <motion.div
                initial={reduceMotion ? false : { opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="text-center mt-16 lg:mt-24 max-w-3xl mx-auto pb-12 lg:pb-16"
            >
                <h3 className="text-xl md:text-2xl xl:text-3xl font-bold tracking-tight">
                    Por eso Carriapp no compara productos. Optimiza tu compra completa.
                </h3>
                <p className="text-base text-muted-foreground mt-4 leading-relaxed">
                    Consideramos precio, formato, promociones, alternativas, membresías y despacho para armar la combinación que realmente te conviene.
                </p>
            </motion.div>
        </section>
    );
};
